import type {
  AskRequest,
  AskResponse,
  ConversationState,
  FormStep,
  GuideChatProduct,
  OpenerRequest,
  OpenerResponse,
  OpenerTrigger,
  PageContext,
  UnavailableResponse,
} from '@/app/lib/guideChat/types';
import { isGuideChatProduct } from '@/app/lib/guideChat/types';
import { CHAT_LABELS, labelsFor } from '@/app/lib/guideChat/labels';
import { isFormLeavingTrigger } from '@/app/lib/guideChat/triggers';

export const FAQ_URL = 'https://reassurez-moi.fr/guide/assurance-pret-immobilier';
export const APPOINTMENT_URL = '/devis-assurance-pret-immobilier';
export const UNAVAILABLE_MESSAGE = CHAT_LABELS.unavailable;

// Where the unavailable notice sends each product's visitor: its guide, and its comparator or partner landing.
export const PRODUCT_URLS: Record<GuideChatProduct, { faqUrl: string; appointmentUrl: string }> = {
  loan: { faqUrl: FAQ_URL, appointmentUrl: APPOINTMENT_URL },
  health: { faqUrl: 'https://reassurez-moi.fr/guide/mutuelle-sante', appointmentUrl: '/devis-mutuelle-sante' },
  travel: { faqUrl: 'https://reassurez-moi.fr/guide/assurance-voyage', appointmentUrl: '/devis-assurance-voyage' },
  provident: { faqUrl: 'https://reassurez-moi.fr/guide/prevoyance', appointmentUrl: '/guide/prevoyance' },
};

export type AskResult =
  | { ok: true; data: AskResponse }
  | { ok: false; unavailable: UnavailableResponse | null; error: string };

function isUnavailable(data: unknown): data is UnavailableResponse {
  return !!data && typeof data === 'object' && (data as { unavailable?: unknown }).unavailable === true;
}

function defaultUnavailable(product: GuideChatProduct = 'loan'): UnavailableResponse {
  return { unavailable: true, message: labelsFor(product).unavailable, ...PRODUCT_URLS[product], product };
}

export async function askGuide(req: AskRequest): Promise<AskResult> {
  const product = isGuideChatProduct(req.product) ? req.product : 'loan';
  try {
    const res = await fetch('/api/poc-guide-chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    });
    const data: unknown = await res.json().catch(() => null);
    if (isUnavailable(data)) return { ok: false, unavailable: { ...defaultUnavailable(product), ...data }, error: data.message };
    if (!res.ok || !data || typeof data !== 'object' || 'error' in (data as object)) {
      const error = (data as { error?: string } | null)?.error || `HTTP ${res.status}`;
      // A dead backend is an outage for the visitor too, never a technical message.
      return { ok: false, unavailable: res.status >= 500 ? defaultUnavailable(product) : null, error };
    }
    return { ok: true, data: data as AskResponse };
  } catch (e) {
    return { ok: false, unavailable: defaultUnavailable(product), error: e instanceof Error ? e.message : String(e) };
  }
}

const PRODUCT_FALLBACK: Record<GuideChatProduct, { noTitle: string; suggestion: string }> = {
  loan: { noTitle: "Une question sur l'assurance emprunteur ? Je peux vous l'expliquer simplement.", suggestion: "Combien coûte une assurance de prêt ?" },
  health: { noTitle: 'Une question sur la mutuelle santé ? Je peux vous l\'expliquer simplement.', suggestion: 'Combien coûte une mutuelle santé ?' },
  travel: { noTitle: "Une question sur l'assurance voyage ? Je peux vous l'expliquer simplement.", suggestion: 'Que couvre une assurance voyage ?' },
  provident: { noTitle: 'Une question sur la prévoyance ? Je peux vous l\'expliquer simplement.', suggestion: 'Quelle prévoyance pour protéger mes proches ?' },
};

const STEP_OPENERS: Record<FormStep, { message: string; suggestions: string[] }> = {
  project: {
    message: "Nouveau prêt ou changement d'assurance ? Je peux vous expliquer la différence en deux phrases.",
    suggestions: ["Quelle différence entre nouveau prêt et changement d'assurance ?", "Puis-je changer d'assurance à tout moment ?"],
  },
  borrower: {
    message: 'Une question sur votre profil (profession, fumeur…) ? Je vous explique ce que cela change pour votre assurance.',
    suggestions: ['Pourquoi me demande-t-on ma profession ?', 'Fumeur ou non-fumeur : quel impact sur le tarif ?'],
  },
  health: {
    message: "Cette étape concerne votre situation de santé. Rassurez-vous : depuis la loi Lemoine, aucun questionnaire médical n'est demandé sous 200 000 € par assuré et avant 60 ans en fin de prêt. Une question ?",
    suggestions: ['Dois-je remplir un questionnaire de santé ?', "Que se passe-t-il si j'ai un antécédent médical ?"],
  },
  loan: {
    message: "Montant, durée, taux : tout est sur votre offre de prêt ou votre tableau d'amortissement. Besoin d'un coup de main ?",
    suggestions: ['Où trouver le capital restant dû ?', "Quel taux indiquer : nominal ou TAEG ?"],
  },
  bank: {
    message: 'La quotité ou les garanties exigées par la banque vous posent question ? Je vous les explique simplement.',
    suggestions: ["C'est quoi la quotité ?", 'Quelles garanties ma banque peut-elle exiger ?'],
  },
  identity: {
    message: 'Une dernière question avant de découvrir vos offres ? Je suis là.',
    suggestions: ['Que faites-vous de mes coordonnées ?', 'Quand vais-je recevoir mes offres ?'],
  },
};

// Three lines in the bubble down to a 360 px wide phone, like the backend openers.
export const OPENER_MAX_CHARS = 85;
export const OPENER_SUGGESTIONS = 2;

function fitTitle(title: string, template: string): string {
  const room = OPENER_MAX_CHARS - (template.length - '%s'.length);
  if (title.length <= room) return template.replace('%s', title);
  const cut = title.slice(0, Math.max(1, room - 1));
  const space = cut.lastIndexOf(' ');
  const word = space > room / 2 ? cut.slice(0, space) : cut;
  return template.replace('%s', `${word.replace(/[\s,;:–—-]+$/, '')}…`);
}

// Used when the backend opener is unavailable: still tied to the page title or the wizard step, never generic.
export function fallbackOpener(ctx: PageContext, trigger?: OpenerTrigger): OpenerResponse {
  const openerId = `op_local_${Date.now()}`;
  if (ctx.kind === 'form' && trigger && isFormLeavingTrigger(trigger)) {
    return {
      openerId,
      message: CHAT_LABELS.formExitOpener,
      suggestions: [CHAT_LABELS.formExitResumeSuggestion, CHAT_LABELS.formExitCallbackSuggestion],
    };
  }
  if (ctx.kind === 'form' && ctx.step) {
    const base = STEP_OPENERS[ctx.step];
    const field = ctx.field && !/^[a-z_]+$/i.test(ctx.field) ? ctx.field : null;
    return { ...base, message: field ? `« ${field} » : ${base.message}` : base.message, openerId };
  }
  if (ctx.kind === 'landing') {
    return {
      openerId,
      message: 'Une question avant de lancer votre simulation ? Je vous réponds en quelques mots.',
      suggestions: ['Quelles informations dois-je préparer ?', "Combien coûte une assurance de prêt en moyenne ?"],
    };
  }
  const title = ctx.title?.trim();
  const fallback = PRODUCT_FALLBACK[isGuideChatProduct(ctx.product) ? ctx.product : 'loan'];
  return {
    openerId,
    message: title ? fitTitle(title, "Une question sur « %s » ? Je vous l'explique.") : fallback.noTitle,
    suggestions: ["Pouvez-vous me résumer l'essentiel ?", fallback.suggestion],
  };
}

export interface OpenerResult {
  opener: OpenerResponse;
  fromBackend: boolean;
  // Minted by the backend when it journals the opener, so the whole visit shares one conversation.
  conversationId: string | null;
}

export async function fetchOpener(req: OpenerRequest): Promise<OpenerResult> {
  try {
    const res = await fetch('/api/poc-guide-chat/opener', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    });
    const data: unknown = await res.json().catch(() => null);
    const opener = data as (Partial<OpenerResponse> & { conversationId?: unknown }) | null;
    if (res.ok && opener && typeof opener.message === 'string' && opener.message.trim()) {
      return {
        opener: {
          message: opener.message,
          suggestions: Array.isArray(opener.suggestions) ? opener.suggestions.filter((s) => typeof s === 'string').slice(0, OPENER_SUGGESTIONS) : [],
          openerId: typeof opener.openerId === 'string' ? opener.openerId : `op_${Date.now()}`,
        },
        fromBackend: true,
        conversationId: typeof opener.conversationId === 'string' ? opener.conversationId : null,
      };
    }
  } catch {
    // Fallback below.
  }
  return { opener: fallbackOpener(req.pageContext, req.trigger), fromBackend: false, conversationId: null };
}

export async function fetchConversationState(conversationId: string): Promise<ConversationState | null> {
  try {
    const res = await fetch(`/api/poc-guide-chat/conversations/${encodeURIComponent(conversationId)}/state`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = (await res.json()) as Partial<ConversationState> | null;
    if (!data || !Array.isArray(data.history)) return null;
    return {
      conversationId: data.conversationId ?? conversationId,
      history: data.history.filter((t) => t && typeof t.question === 'string' && typeof t.answer === 'string'),
      collected: data.collected ?? {},
      funnelStage: data.funnelStage ?? null,
      lastActivityAt: data.lastActivityAt ?? null,
      product: isGuideChatProduct(data.product) ? data.product : null,
    };
  } catch {
    return null;
  }
}

export type { OpenerTrigger };
